import { useClerk } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useRouter } from "next/router";
import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { RouteConfig } from "@/config/route.config";
import type { ProfileModels } from "@/data/profile/profile.models";
import { ProfileQueries } from "@/data/profile/profile.queries";
import type { UserModels } from "@/data/user/user.models";
import { UserQueries } from "@/data/user/user.queries";
import { useClerkErrorHandler } from "@/hooks/useClerkErrorHandler";
import { logger } from "@/util/logger";
import { AppRestClient } from "@/util/rest/clients/app-rest-client";
import { CanisRestClient } from "@/util/rest/clients/canis-rest-client";
import { AuthorizationHeaderInterceptor } from "@/util/rest/interceptors/authorization-header.interceptor";
import { TokenExpiredInterceptor } from "@/util/rest/interceptors/token-expired.interceptor";

export namespace AuthContext {
  export interface Type {
    isLoggedIn: boolean;
    user: ProfileModels.FullProfileResponseDto | undefined;
    userProfile: UserModels.UserAccountResponse | undefined;
    isInitializing: boolean;
    isTokenApplied: boolean;
    shouldPerformSync: boolean;
    syncProfile: () => Promise<void>;
    logout: () => Promise<void>;
  }

  const Context = createContext<Type>({} as never);

  interface IProps {
    shouldPerformSync: boolean;
  }

  export const Provider = ({ children, shouldPerformSync }: PropsWithChildren<IProps>) => {
    const [user, setUser] = useState<ProfileModels.FullProfileResponseDto | undefined>(undefined);
    const [userProfile, setUserProfile] = useState<UserModels.UserAccountResponse | undefined>(undefined);

    const router = useRouter();
    const { session, loaded: clerkLoaded, signOut, client: clerkClient } = useClerk();
    const { handleClerkError } = useClerkErrorHandler();

    // Skip user/me call on verify pages
    const isVerifyPage =
      router.pathname === "/sign-in/verify" ||
      router.pathname === "/sign-up/verify" ||
      router.pathname === "/sign-up/sso-callback" ||
      router.pathname === "/sign-in/sso-callback" ||
      router.pathname === "/postsso" ||
      router.pathname === "/beta/check" ||
      router.pathname === "/beta/ineligible";

    const [isClerkLoadingUp, setIsClerkLoadingUp] = useState<boolean>(false);
    const [isClerkLoggedIn, setIsClerkLoggedIn] = useState<boolean>(false);
    const [isClerkTokenApplied, setIsClerkTokenApplied] = useState<boolean>(false);
    const [isLoadingUserProfile, setIsLoadingUserProfile] = useState<boolean>(true);
    const [isUserLoggedInAndFetched, setIsUserLoggedInAndFetched] = useState<boolean>(false);

    const isSessionDefined = session !== null;
    const ldClient = useLDClient();

    const { refetch: refetchProfile } = ProfileQueries.useGetMy<ProfileModels.FullProfileResponseDto>({
      enabled: false,
    });

    const { refetch: refetchUser } = UserQueries.useGet<UserModels.UserAccountResponse>({
      enabled: false,
    });

    const identifyUser = useCallback(
      (loggedAuthUser: typeof user) => {
        if (!loggedAuthUser) {
          return;
        }
        ldClient?.identify({
          key: loggedAuthUser?.user.email ?? "",
          kind: "user",
          name: loggedAuthUser?.user.name ?? "",
          email: loggedAuthUser?.user.email ?? "",
        });
      },
      [ldClient],
    );

    useEffect(() => {
      // we dientify user in LD once we have user data
      // this enables us to enable/disable flags per email address
      if (isUserLoggedInAndFetched && user) {
        identifyUser(user);
      }
    }, [identifyUser, isUserLoggedInAndFetched, user]);

    useEffect(() => {
      if (!clerkLoaded) {
        setIsClerkLoadingUp(true);
      } else {
        setIsClerkLoadingUp(false);
        if (isSessionDefined) {
          setIsClerkLoggedIn(true);
        } else {
          setIsClerkLoggedIn(false);
          setIsUserLoggedInAndFetched(false);
          setUser(undefined);
          setIsLoadingUserProfile(false);
        }
      }
    }, [clerkLoaded, isSessionDefined]);

    const resetAuthState = useCallback(() => {
      setUser(undefined);
      setIsLoadingUserProfile(false);
      setIsUserLoggedInAndFetched(false);
      setUser(undefined);
      // setSubscriptionData(undefined);
      setIsClerkTokenApplied(false);
      setIsClerkLoggedIn(false);
    }, []);

    const queryClient = useQueryClient();

    const logoutAsync = useCallback(async () => {
      // 1. Cancel ALL ongoing queries immediately
      await queryClient.cancelQueries();

      // 2. Remove all cached data to prevent stale queries
      queryClient.clear();

      // 3. Immediately disable auth state
      resetAuthState();

      // 4. Remove interceptors first to prevent further requests carrying tokens or triggering 401 flows
      AppRestClient.ejectInterceptor(AuthorizationHeaderInterceptor);
      AppRestClient.ejectInterceptor(TokenExpiredInterceptor);

      CanisRestClient.ejectInterceptor(AuthorizationHeaderInterceptor);
      CanisRestClient.ejectInterceptor(TokenExpiredInterceptor);

      // 5. Then sign out and navigate
      await signOut({ redirectUrl: RouteConfig.Signin });

      // 7. Destroy Clerk client
      await clerkClient.destroy();
    }, [signOut, resetAuthState, queryClient, clerkClient]);

    const syncProfile = useCallback(async () => {
      const fetchedUser = await refetchProfile();
      if (fetchedUser.data) {
        setUser(fetchedUser.data);
      }
    }, [refetchProfile]);

    const onAccessTokenExpired = useCallback(async () => {
      await logoutAsync();
    }, [logoutAsync]);

    const applyAccessToken = useCallback(
      (tokenSession: typeof session) => {
        const getAuthToken = async () => {
          const token = await tokenSession?.getToken();
          return token ? `Bearer ${token}` : null;
        };

        AppRestClient.attachInterceptor(AuthorizationHeaderInterceptor, getAuthToken);
        AppRestClient.attachInterceptor(TokenExpiredInterceptor, onAccessTokenExpired);

        CanisRestClient.attachInterceptor(AuthorizationHeaderInterceptor, getAuthToken);
        CanisRestClient.attachInterceptor(TokenExpiredInterceptor, onAccessTokenExpired);

        setIsClerkTokenApplied(true);
      },
      [onAccessTokenExpired],
    );

    useEffect(() => {
      if (!isClerkLoggedIn || !session) {
        return;
      }

      if (isClerkTokenApplied && isUserLoggedInAndFetched) {
        return;
      }

      setTimeout(() => {
        applyAccessToken(session);
      }, 200);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClerkLoggedIn, session, isClerkTokenApplied, isUserLoggedInAndFetched]);

    useEffect(() => {
      if (!clerkLoaded || !isClerkLoggedIn || !isClerkTokenApplied) {
        return;
      }

      // Skip user/me call on verify pages
      if (isVerifyPage) {
        setIsLoadingUserProfile(false);
        return;
      }

      const tryRefetchProfile = async () => {
        try {
          const user = await refetchUser();
          if (user.isError && user.error?.serverMessage === "Users identity not found") {
            logger.warn("User is not registered, logging out");
            await logoutAsync();
            return;
          }

          if (user.data) {
            setUserProfile(user.data);
          }

          const profile = await refetchProfile();

          if (profile.data) {
            setUser(profile.data);
            setIsUserLoggedInAndFetched(true);
            setIsLoadingUserProfile(false);
          }
        } catch (error) {
          logger.error("Something went wrong while refetching the profile", error);
          // Handle potential Clerk-related errors during profile fetch
          handleClerkError(error, "tryRefetchProfile");
          setIsLoadingUserProfile(false);
        }
      };

      setIsLoadingUserProfile(true);
      setTimeout(() => {
        tryRefetchProfile();
      }, 400);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clerkLoaded, isClerkLoggedIn, isClerkTokenApplied, isVerifyPage]);

    const value = useMemo(
      (): Type => ({
        isLoggedIn: isUserLoggedInAndFetched,
        user,
        userProfile,
        isInitializing: isClerkLoadingUp || isLoadingUserProfile,
        isTokenApplied: isClerkTokenApplied,
        syncProfile,
        logout: logoutAsync,
        shouldPerformSync,
      }),
      [
        isUserLoggedInAndFetched,
        user,
        userProfile,
        isClerkLoadingUp,
        isLoadingUserProfile,
        isClerkTokenApplied,
        syncProfile,
        logoutAsync,
        shouldPerformSync,
      ],
    );

    let content = null;

    if (!isClerkLoadingUp) {
      content = children;
    }

    return <Context.Provider value={value}>{content}</Context.Provider>;
  };

  export const useAuth = () => {
    const auth = useContext(Context);
    return auth;
  };
}
